import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
  blockItalicSecondaryNoteClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/Four Seasons Facade System/images";

export default function FourSeasonsFacadePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mullionTypes = [
    `${BASE}/mw1.png`,
    `${BASE}/mw2.png`,
    `${BASE}/mw3.png`,
    `${BASE}/mw4.png`,
    `${BASE}/mw5.png`,
    `${BASE}/mw6.png`,
    `${BASE}/mw7.png`,
    `${BASE}/mw8.png`,
  ];
  const mullionCaps = mullionTypes.map((_, i) => `Mullion type ${String(i + 1).padStart(2, "0")}`);

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="Four Seasons Facade"
          description="An automated digital-fabrication system for the Four Seasons Dubai Water Canal Hotel facade — taking the architect’s base surfaces and resolving them to LOD 500, with mullion geometry and fabrication-ready data emerging as outputs."
          meta={["Dubai, UAE", "Studio Libeskind", "GENx", "Catia (3DExperience)", "X-Generative", "EKL", "2022"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/Card_Image.png`}
                alt="Four Seasons Facade — primary visualisation"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Project scope">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "GENx is a New York– and Istanbul–based innovation hub committed to Industry 4.0, with partners in London and Sofia. The studio delivers BIM-integrated, case-specific project management solutions.",
              "On this project, the goal was an automated digital-fabrication system for the Four Seasons Dubai Water Canal Hotel facade — a pipeline that could absorb architect-side geometry changes well into late design without manual re-modelling. From day one the brief was singular: 100% automated solving of the facade geometry, and 100% automated production of its fabrication data.",
            ]}
          />
        </BlockSection>

        <BlockSection title="Technical approach">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The system takes the architect’s base surfaces and processes them to LOD 500, returning organised data ready for fabrication and construction management. It has two parts.",
              "First, fully customisable and adaptive UDFs (User-Defined Features) are authored in Catia for every facade mullion type designed by the architect. Second, X-Generative divides each facade into properly sized panels and categorises them; the categorised panels then drive the mullion UDFs to produce the actual fabrication geometry.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/diagram 11-Photoshopped.png`} alt="Pipeline diagram" ratio="classic" fit="contain" caption="Pipeline diagram" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/details to udfs.png`} alt="Details translated to UDFs" ratio="classic" fit="contain" caption="Details to UDFs" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Mullion library">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Each mullion type is authored as a parametric UDF — a single component that absorbs site-specific angles, lengths, and connection conditions and rebuilds the appropriate fabrication geometry on the fly.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <ImageSequence
                  images={mullionTypes}
                  captions={mullionCaps}
                  label="Parametric mullions"
                  ratio="video"
                />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Parametric Mullions.PNG`} alt="Parametric mullion configurator" ratio="classic" fit="contain" caption="Parametric configurator" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Changing Mullion.PNG`} alt="Mullion changing under parameter sweep" ratio="classic" fit="contain" caption="Parameter sweep" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Panel categorisation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "X-Generative divides each facade into panels and tags every panel by type — a typical / atypical split that downstream stages use to decide which UDF to instantiate.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/typical example.png`} alt="Typical facade region" ratio="classic" fit="contain" caption="Typical region" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/nontypical example 2PSed.png`} alt="Non-typical facade region" ratio="classic" fit="contain" caption="Non-typical region" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/types highlight.png`} alt="Type-highlight across facade" ratio="banner" fit="contain" caption="Type highlight" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Renders and visualisations">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/AL-1-Beach-View.jpg`} alt="Four Seasons — beach view render" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/nl-1-night-view.jpg`} alt="Four Seasons — night view render" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/el-3-up-close-view.jpg`} alt="Four Seasons — close-up view render" ratio="banner" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>

          <p className={blockItalicSecondaryNoteClass}>
            Architect: Studio Libeskind. Facade consultant: GENx Design and Technology.
          </p>
        </BlockSection>
      </div>
    </main>
  );
}
